// Which texture files an FBX model asks for, read in the browser before anything is uploaded.
// The renderer names the same files (`mappedTextures` on the model doc) and stops the preview
// with renderModelError + `missingTextures` when one of them was not uploaded, so a gap found
// here saves the user a whole round trip.
//
// Binary FBX (6.x and 7.x) and ASCII FBX. Only the small Texture / Video records are read; the
// geometry is jumped over by the offsets every record carries, so a 400 MB model costs a handful
// of small reads. Kept free of imports and of non-erasable TypeScript so that node runs it as is
// (docs/render-landing-port.md, "Проверка текстур FBX").

export interface FbxTextureRef {
  // The file name the model asks for ('Wood_BaseColor.png'), the way the renderer reports it.
  name: string;
  // Every name the texture goes by: RelativeFilename and FileName may disagree.
  names: string[];
  // Packed inside the FBX (its Video carries Content): nothing to upload.
  embedded: boolean;
}

export interface FbxTextureScan {
  format: 'binary' | 'ascii';
  version: number;
  textures: FbxTextureRef[];
  // The program that wrote the file (SceneInfo's Original|ApplicationName and …Version):
  // { name: 'Blender (stable FBX IO)', version: '2.76 (sub 0)' }, { name: 'Maya', version: '201800' }.
  // Every export from Blender, Maya and 3ds Max carries it; files written by the FBX SDK itself
  // (converters, test sets) leave it empty, and then it is absent.
  app?: { name: string; version: string };
}

// Past this an ASCII FBX (rare: exporters write binary by default) is not read at all.
const ASCII_READ_LIMIT_BYTES = 150 * 1024 * 1024;
// The header is 'Kaydara FBX Binary  \0'; some exporters get the padding wrong.
const BINARY_MAGIC = 'Kaydara FBX Binary';
const WINDOW_BYTES = 1024 * 1024;

// null: not an FBX this reader understands. The caller then skips the check and leaves it to
// the renderer, which is what happened before this existed.
export async function scanFbxTextures(blob: Blob): Promise<FbxTextureScan | null> {
  try {
    const reader = new WindowReader(blob);
    const head = await reader.bytes(0, Math.min(27, blob.size));
    if (latin1(head.subarray(0, BINARY_MAGIC.length)) === BINARY_MAGIC) {
      return await scanBinary(reader, head);
    }
    if (blob.size > ASCII_READ_LIMIT_BYTES) {
      return null;
    }
    const text = await blob.text();
    return /FBXHeaderExtension|; FBX \d/.test(text.slice(0, 4096)) ? scanAscii(text) : null;
  } catch {
    return null;
  }
}

export interface FbxTextureCheck {
  // Asked for, and no file of the drop carries the name.
  missing: FbxTextureRef[];
  // Asked for and dropped, but outside the model's folder: `path` is where the file is.
  misplaced: { texture: FbxTextureRef; path: string }[];
  // Asked for and there: in the model's folder, or inside the FBX itself.
  found: FbxTextureRef[];
}

// Holds the model's texture list against the dropped files, the renderer's way (measured on the
// dev model docs, 2026-09-29): a texture is found by its file name anywhere under the model's own
// folder, and a file beside that folder ('../textures/x.png') counts as missing. Letter case is
// ignored, as in the library's missing-textures dialog, and so is the Unicode form: macOS may
// hand over 'é' as e + a combining accent.
export function checkTextures(scan: FbxTextureScan, modelPath: string, files: { path: string }[]): FbxTextureCheck {
  const folder = folderOf(modelPath);
  const inside = new Set<string>();
  const elsewhere = new Map<string, string>();
  for (const file of files) {
    const key = baseName(file.path).normalize('NFC').toLowerCase();
    if (!folder || normalize(file.path).startsWith(folder + '/')) {
      inside.add(key);
    } else if (!elsewhere.has(key)) {
      elsewhere.set(key, file.path);
    }
  }
  const check: FbxTextureCheck = { missing: [], misplaced: [], found: [] };
  for (const texture of scan.textures) {
    const keys = texture.names.map((name) => name.normalize('NFC').toLowerCase());
    if (texture.embedded || keys.some((key) => inside.has(key))) {
      check.found.push(texture);
      continue;
    }
    const found = keys.find((key) => elsewhere.has(key));
    if (found) {
      check.misplaced.push({ texture, path: elsewhere.get(found) as string });
    } else {
      check.missing.push(texture);
    }
  }
  return check;
}

// 'Chair/source/chair.fbx' → 'Chair/source'; a lone file → ''.
export function folderOf(path: string): string {
  const parts = normalize(path).split('/');
  return parts.slice(0, -1).join('/');
}

function normalize(path: string): string {
  return path.replace(/\\/g, '/').replace(/^\/+/, '');
}

// --- binary ---

interface RawNode {
  name: string;
  start: number;
  end: number;
  propsStart: number;
  propsCount: number;
  childrenStart: number;
}

interface RawTexture {
  names: string[];
}

async function scanBinary(reader: WindowReader, head: Uint8Array): Promise<FbxTextureScan> {
  const version = new DataView(head.buffer, head.byteOffset, head.byteLength).getUint32(23, true);
  const wide = version >= 7500;
  const textures: RawTexture[] = [];
  const videos: { names: string[]; embedded: boolean }[] = [];
  let app: FbxTextureScan['app'];

  for (const top of await children(reader, 27, reader.size, wide)) {
    if (top.name === 'FBXHeaderExtension') {
      app = await binaryApp(reader, top, wide);
      continue;
    }
    if (top.name !== 'Objects') {
      continue;
    }
    for (const object of await children(reader, top.childrenStart, top.end, wide)) {
      if (object.name !== 'Texture' && object.name !== 'Video') {
        continue;
      }
      const names: string[] = [];
      let embedded = false;
      for (const field of await children(reader, object.childrenStart, object.end, wide)) {
        if (field.name === 'FileName' || field.name === 'Filename' || field.name === 'RelativeFilename') {
          const value = await firstStringProp(reader, field);
          if (value) {
            names.push(value);
          }
        } else if (field.name === 'Content' && field.propsCount > 0) {
          embedded = embedded || (await firstPropLength(reader, field)) > 0;
        }
      }
      if (object.name === 'Texture') {
        textures.push({ names });
      } else {
        videos.push({ names, embedded });
      }
    }
  }
  return { format: 'binary', version, textures: resolve(textures, videos), ...(app ? { app } : {}) };
}

// FBXHeaderExtension › SceneInfo › Properties70 (Properties60 in 6.x) › P ("Property" in 6.x):
// the property's name, its type and flags, then the value — the last string.
async function binaryApp(reader: WindowReader, header: RawNode, wide: boolean): Promise<FbxTextureScan['app']> {
  const values = new Map<string, string>();
  for (const info of await children(reader, header.childrenStart, header.end, wide)) {
    if (info.name !== 'SceneInfo') {
      continue;
    }
    for (const list of await children(reader, info.childrenStart, info.end, wide)) {
      if (!list.name.startsWith('Properties')) {
        continue;
      }
      for (const property of await children(reader, list.childrenStart, list.end, wide)) {
        const strings = await stringProps(reader, property);
        if (strings.length > 1 && APP_KEYS.includes(strings[0])) {
          values.set(strings[0], strings[strings.length - 1].trim());
        }
      }
    }
  }
  return appOf(values.get(APP_KEYS[0]) || '', values.get(APP_KEYS[1]) || '');
}

const APP_KEYS = ['Original|ApplicationName', 'Original|ApplicationVersion'];

// Bytes of each fixed-size property type; arrays (f d l i b) carry their own length.
const PROP_BYTES: Record<string, number> = { Y: 2, C: 1, I: 4, F: 4, D: 8, L: 8 };

// A record's string properties, in order; the others are stepped over.
async function stringProps(reader: WindowReader, node: RawNode): Promise<string[]> {
  const strings: string[] = [];
  let at = node.propsStart;
  for (let i = 0; i < node.propsCount && at < node.childrenStart; i++) {
    const type = String.fromCharCode((await reader.view(at, 1)).getUint8(0));
    at += 1;
    if (type === 'S' || type === 'R') {
      const length = (await reader.view(at, 4)).getUint32(0, true);
      if (type === 'S') {
        strings.push(utf8(await reader.bytes(at + 4, length)));
      }
      at += 4 + length;
    } else if (PROP_BYTES[type] !== undefined) {
      at += PROP_BYTES[type];
    } else if ('fdlib'.includes(type)) {
      at += 12 + (await reader.view(at, 12)).getUint32(8, true);
    } else {
      break;
    }
  }
  return strings;
}

function appOf(name: string, version: string): FbxTextureScan['app'] {
  return name ? { name, version } : undefined;
}

// The records between `start` and `end`, headers only.
async function children(reader: WindowReader, start: number, end: number, wide: boolean): Promise<RawNode[]> {
  const headerBytes = wide ? 25 : 13;
  const nodes: RawNode[] = [];
  let at = start;
  while (at + headerBytes <= end) {
    const view = await reader.view(at, headerBytes);
    const endOffset = wide ? u64(view, 0) : view.getUint32(0, true);
    if (endOffset === 0) {
      break; // the null record that closes a list
    }
    const propsCount = wide ? u64(view, 8) : view.getUint32(4, true);
    const propsLength = wide ? u64(view, 16) : view.getUint32(8, true);
    const nameLength = view.getUint8(wide ? 24 : 12);
    const name = latin1(await reader.bytes(at + headerBytes, nameLength));
    const propsStart = at + headerBytes + nameLength;
    if (endOffset <= at || endOffset > reader.size) {
      throw new Error('Broken FBX record');
    }
    nodes.push({ name, start: at, end: endOffset, propsStart, propsCount, childrenStart: propsStart + propsLength });
    at = endOffset;
  }
  return nodes;
}

async function firstStringProp(reader: WindowReader, node: RawNode): Promise<string> {
  const view = await reader.view(node.propsStart, 5);
  if (String.fromCharCode(view.getUint8(0)) !== 'S') {
    return '';
  }
  const length = view.getUint32(1, true);
  return utf8(await reader.bytes(node.propsStart + 5, length));
}

// Content is one 'R' (raw) property: its length is all that matters, the bytes are not read.
async function firstPropLength(reader: WindowReader, node: RawNode): Promise<number> {
  const view = await reader.view(node.propsStart, 5);
  const type = String.fromCharCode(view.getUint8(0));
  return type === 'R' || type === 'S' ? view.getUint32(1, true) : 0;
}

// --- ASCII ---

function scanAscii(text: string): FbxTextureScan {
  const version = Number((/FBXVersion: *(\d+)/.exec(text) || [])[1] || 0);
  const textures: RawTexture[] = [];
  const videos: { names: string[]; embedded: boolean }[] = [];
  const opening = /^[ \t]*(Texture|Video):[^{\n]*\{/gm;
  let match: RegExpExecArray | null;
  while ((match = opening.exec(text))) {
    const body = text.slice(opening.lastIndex, blockEnd(text, opening.lastIndex));
    const names: string[] = [];
    const field = /^[ \t]*(?:FileName|Filename|RelativeFilename): *"([^"]*)"/gm;
    let found: RegExpExecArray | null;
    while ((found = field.exec(body))) {
      if (found[1]) {
        names.push(found[1]);
      }
    }
    if (match[1] === 'Texture') {
      textures.push({ names });
    } else {
      videos.push({ names, embedded: /^[ \t]*Content: *,?\s*"[^"]/m.test(body) });
    }
    opening.lastIndex += body.length;
  }
  const app = appOf(asciiProperty(text, APP_KEYS[0]), asciiProperty(text, APP_KEYS[1]));
  return { format: 'ascii', version, textures: resolve(textures, videos), ...(app ? { app } : {}) };
}

// 'P: "Original|ApplicationName", "KString", "", "", "Maya"' → 'Maya'. The header sits at the top.
function asciiProperty(text: string, key: string): string {
  const head = text.slice(0, 65536);
  const at = head.indexOf(`"${key}"`);
  if (at < 0) {
    return '';
  }
  const end = head.indexOf('\n', at);
  const values = head.slice(at, end < 0 ? undefined : end).match(/"[^"]*"/g) || [];
  return values.length > 1 ? values[values.length - 1].slice(1, -1).trim() : '';
}

// Index just past the brace that closes the block opened before `from`; quoted text is skipped.
function blockEnd(text: string, from: number): number {
  let depth = 1;
  let quoted = false;
  for (let i = from; i < text.length; i++) {
    const char = text[i];
    if (char === '"') {
      quoted = !quoted;
    } else if (!quoted && char === '{') {
      depth++;
    } else if (!quoted && char === '}' && --depth === 0) {
      return i;
    }
  }
  return text.length;
}

// --- both ---

// One entry per file name. A texture is embedded when a Video under the same name carries the
// image. A name the reader could not decode is dropped rather than reported missing.
function resolve(textures: RawTexture[], videos: { names: string[]; embedded: boolean }[]): FbxTextureRef[] {
  const packed = new Set<string>();
  for (const video of videos) {
    if (video.embedded) {
      video.names.forEach((name) => packed.add(baseName(name).toLowerCase()));
    }
  }
  const byName = new Map<string, FbxTextureRef>();
  for (const texture of textures) {
    const names = unique(texture.names.map(baseName).filter((name) => name && !name.includes('�')));
    if (names.length === 0) {
      continue;
    }
    const key = names[0].toLowerCase();
    if (byName.has(key)) {
      continue;
    }
    byName.set(key, { name: names[0], names, embedded: names.some((name) => packed.has(name.toLowerCase())) });
  }
  return Array.from(byName.values());
}

function baseName(path: string): string {
  const parts = path.split(/[\\/]/);
  return parts[parts.length - 1].trim();
}

function unique(values: string[]): string[] {
  const seen = new Set<string>();
  return values.filter((value) => {
    const key = value.toLowerCase();
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
}

function u64(view: DataView, offset: number): number {
  return view.getUint32(offset, true) + view.getUint32(offset + 4, true) * 0x100000000;
}

function latin1(bytes: Uint8Array): string {
  let out = '';
  for (const byte of bytes) {
    out += String.fromCharCode(byte);
  }
  return out;
}

function utf8(bytes: Uint8Array): string {
  return new TextDecoder('utf-8').decode(bytes);
}

// Reads a Blob a window at a time: records sit close together, so most reads hit the window.
class WindowReader {
  public readonly size: number;
  private readonly blob: Blob;
  private window = new Uint8Array(0);
  private windowStart = 0;

  constructor(blob: Blob) {
    this.blob = blob;
    this.size = blob.size;
  }

  public async bytes(offset: number, length: number): Promise<Uint8Array> {
    if (offset + length > this.size) {
      throw new Error('Read past the end of the FBX');
    }
    if (offset < this.windowStart || offset + length > this.windowStart + this.window.length) {
      const end = Math.min(this.size, offset + Math.max(length, WINDOW_BYTES));
      this.window = new Uint8Array(await this.blob.slice(offset, end).arrayBuffer());
      this.windowStart = offset;
    }
    return this.window.subarray(offset - this.windowStart, offset - this.windowStart + length);
  }

  public async view(offset: number, length: number): Promise<DataView> {
    const bytes = await this.bytes(offset, length);
    return new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  }
}
